import React, { useEffect, useState } from 'react';
import { Translation, getI18n } from 'react-i18next';
import {
  Checkbox,
  Typography,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Tag,
  Select,
  Tooltip,
} from 'antd';
import { withRouter } from 'react-router';

import Logger from '../../../../../lib/Logger';
import notification from '../../../elements/lib/NotificationWrapper';
import LearningAdminModuleSerieFormFooter from './LearningAdminModuleSerieFormFooter';
import { PublicStatusTag } from '../../../elements/components/Tags';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { pathTo } from '../../../Routes';
import QueryString from '../../../../../lib/QueryString';

const { Title } = Typography;

const layout = {
  main: {
    wrapperCol: { span: 24 },
  },
};
const optionsPublicStatus = [1, 2, 3];

const LearningAdminModuleSerieFormStep1 = ({
  id,
  load,
  created_id,
  partnershipId,
  data,
  isSubmitting,
  errors,
  destroyForm,
  isLoading,
  setConfirmationModalOpen,
  isChangeValue,
  modules,
  step,
  setIsChangeValue,
  oldParams,
  setTargetPath,
  nextStep,
  setModuleSerieId,
  history,
  ...props
}) => {
  const [form] = Form.useForm();

  const [publicStatus, setPublicStatus] = useState(2);

  // load record data from API
  useEffect(() => {
    if (id) {
      setModuleSerieId(id);
      load(partnershipId, id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      form.setFieldsValue(dataObj);

      setPublicStatus(dataObj['public_status']);
    }
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setModuleSerieId(created_id);
      setIsChangeValue(false);
      nextStep();
    }
    return () => {
      destroyForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created_id, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `LearningAdminModuleSerieFormStep1.submitData(###)`);

    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {
        if (['is_recommended'].includes(input)) {
          payload[input] = values[input] ? true : false;
        } else {
          payload[input] = values[input];
        }
      }
    }

    payload['partnership_id'] = partnershipId;

    if (id) {
      // update
      props.update(id, payload, (success) => {
        if (success) {
          notification.success(getI18n().t('feedback_form_success'));
          setIsChangeValue(false);
          nextStep();
          destroyForm();
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      // create
      props.create(payload, (success) => {
        if (success) {
          notification.success(getI18n().t('feedback_form_success'));
        } else {
          notification.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `LearningAdminModuleSerieFormStep1.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log(
      'debug',
      `LearningAdminModuleSerieFormStep1.handleFinishFailed(###)`
    );
    notification.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    for (const key of Object.keys(changedValues)) {
      form.setFields([{ name: key, errors: [] }]);
    }
    setIsChangeValue(true);
  };

  const onClickCancel = () => {
    const path = QueryString.append(pathTo('LearningsAdminScreen'), oldParams);

    if (isChangeValue) {
      setTargetPath(path);
      setConfirmationModalOpen(true);
    } else {
      history.push(path);
    }
  };

  return (
    <Translation>
      {(t) => (
        <div className='module-serie-form-step-1'>
          <Form
            name='learning_admin_module_serie_form_step_1'
            form={form}
            initialValues={data}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            onValuesChange={handleValuesChange}
            validateTrigger='onSubmit'
            {...layout.main}
          >
            <Row justify='space-between' align='middle'>
              <Col>
                <Title level={5}>
                  {t('learning_admin_form_module_serie_title_step_1')}
                </Title>
              </Col>
              <Col>
                {id ? (
                  <div
                    className='form-group'
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Form.Item name='public_status' noStyle>
                      <Select
                        bordered={false}
                        showArrow
                        dropdownMatchSelectWidth={false}
                        className='public-status-select'
                      >
                        {optionsPublicStatus.map((x) => (
                          <Select.Option key={x} value={x}>
                            <PublicStatusTag value={x} />
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {publicStatus === 3 ? (
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className='cursor-pointer module-action-icon-delete'
                      />
                    ) : (
                      <Tooltip
                        placement='topRight'
                        title={t('learning_module_series_message_info_delete')}
                        arrowPointAtCenter
                      >
                        <FontAwesomeIcon
                          icon={faTrashCan}
                          className='module-action-icon-delete-disabled'
                        />
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <>
                    <Form.Item name='public_status' noStyle>
                      <Input type='hidden' />
                    </Form.Item>
                    <Tag color='blue'>{t('learning_public_status_draft')}</Tag>
                  </>
                )}
              </Col>
            </Row>

            <div className='form-group'>
              <div className='ant-col ant-col-24 ant-form-item-label'>
                <label className='ant-form-item-required'>
                  {t('learning_admin_form_input_title')}
                </label>
              </div>
              <Form.Item
                name='title'
                rules={[
                  {
                    required: true,
                    message: t('feedback_validation_required'),
                  },
                  {
                    type: 'string',
                    min: 1,
                    max: 60,
                    message: t('feedback_validation_char_range', {
                      min: 1,
                      max: 60,
                    }),
                  },
                ]}
              >
                <Input disabled={isLoading || isSubmitting} />
              </Form.Item>
            </div>
            <div className='form-group'>
              <div className='ant-col ant-col-24 ant-form-item-label'>
                <label className='ant-form-item-required'>
                  {t('learning_admin_form_input_order')}
                </label>
              </div>
              <Form.Item
                name='order'
                rules={[
                  {
                    required: true,
                    message: t('feedback_validation_required'),
                  },
                  {
                    type: 'number',
                    message: t('feedback_validation_number'),
                  },
                  {
                    type: 'number',
                    min: 0,
                    message: t('feedback_validation_range', {
                      min: 0,
                    }),
                  },
                ]}
              >
                <InputNumber min={0} disabled={isLoading || isSubmitting} />
              </Form.Item>
            </div>
            <div className='form-group'>
              <Form.Item name='is_recommended' valuePropName='checked'>
                <Checkbox disabled={isLoading || isSubmitting}>
                  {t('learning_admin_form_input_is_recommended')}
                </Checkbox>
              </Form.Item>
            </div>

            <LearningAdminModuleSerieFormFooter
              id={id}
              disabled={isLoading || isSubmitting}
              module={modules[step]}
              onClickCancel={onClickCancel}
            />
          </Form>
        </div>
      )}
    </Translation>
  );
};

export default withRouter(LearningAdminModuleSerieFormStep1);
